import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router';

import { Typography } from '@mui/material';
import { GridRowModel } from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';

import { useCarrinhoContext } from '../contexts';

import { useFormMutation } from 'mutations/useFormMutation';
import { CarrinhoStockErrorText } from 'pages/compra/carrinho/components';

export type ProductStock = {
    cdProduto: string;
    dsProduto: string;
    sgUnidadeMedida: string;
    qtEstoqueDisponivel: number;
};

export function useFinishPedido({ row }: { row?: GridRowModel } = {}) {
    const { mutate } = useFormMutation();
    const {
        setDisabledActions,
        setOpenFinishedSuccessModal,
        labelFinishedSuccessModal,
        contentFinishedErrorModal,
        setOpenFinishedErrorModal,
        selectedPedidos,
        setSelectedPedidos,
        pedidos,
        isFinishAllPolling,
        setIsFinishAllPolling,
    } = useCarrinhoContext();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [finishingPedidoIds, setFinishingPedidoIds] = useState<number[]>([]);

    /**
     * ao atualizar pedidos, caso esteja em estado de pooling para finalizar todos os pedidos, valida se todos foram finalizados com sucesso
     * caso tenha erro em algum pedido, exibe modal com erro e desabilita botão de finalizar todos
     * caso todos foram finalizados com sucesso, exibe modal com sucesso e limpa seleção de pedidos
     */
    const validateFinishAllPooling = () => {
        // este hook também é usado pelas ações de cada linha. Apenas a instância
        // da barra inicia e acompanha a finalização em lote.
        if (row) return;

        if (!isFinishAllPolling) return;

        const idsFinalizados = new Set(finishingPedidoIds);
        const pedidosDaFinalizacao = pedidos.filter((pedido) => idsFinalizados.has(pedido.idPedido));
        const hasPedidoFinalizando = pedidosDaFinalizacao.some((pedido) => pedido.pedidoStatus.idPedidoStatus === 19); // 19 = Finalizando

        if (hasPedidoFinalizando) return;

        const hasPedidoFalha = pedidosDaFinalizacao.some((pedido) => Boolean(pedido.pedidoLog));

        if (hasPedidoFalha) {
            contentFinishedErrorModal.current = (
                <Typography sx={{ color: 'grey.600' }}>
                    Alguns pedidos não puderam ser finalizados.
                    <br />
                    Verifique o erro clicando em "Falha ao Finalizar" em cada pedido.
                </Typography>
            );

            setOpenFinishedErrorModal(true);
        } else {
            labelFinishedSuccessModal.current = 'Pedido(s) finalizado(s) com sucesso!';
            setOpenFinishedSuccessModal(true);
            setSelectedPedidos([]);
        }

        setIsFinishAllPolling(false);
        setDisabledActions(false);
        setFinishingPedidoIds([]);

        queryClient.invalidateQueries({ queryKey: ['carrinho-totalizador'] });
    };

    useEffect(validateFinishAllPooling, [pedidos]);

    /**
     * valida se o erro retornado é de estoque insuficiente.
     */
    const applyStockErrorText = (errors: ProductStock[]) => Array.isArray(errors) && errors.at(0)?.qtEstoqueDisponivel !== undefined;

    /**
     * redireciona para a tela de pagamento, passando os ids dos pedidos a serem finalizados.
     */
    const redirectToPagamento = (idsPedido: number[]) => navigate(`/compra/pagamento?idsPedido=${idsPedido.join(',')}`);

    /**
     * - finaliza um pedido.
     * - se sucesso, exibe modal de sucesso e atualiza dados do carrinho.
     * - se erro, exibe modal de erro com a mensagem retornada da API ou uma mensagem genérica.
     */
    const mutateFinishPedido = () => {
        setDisabledActions(true);

        mutate(
            { url: `/compra/pedidocompra/rascunho/${row!.idPedido}/finalizar`, preventSnack: true },
            {
                onSuccess: () => {
                    labelFinishedSuccessModal.current = 'Pedido finalizado com sucesso!';

                    setSelectedPedidos([]);
                    setOpenFinishedSuccessModal(true);

                    queryClient.invalidateQueries({ queryKey: ['carrinho'] });
                    queryClient.invalidateQueries({ queryKey: ['carrinho-totalizador'] });
                },
                onError: (error: any) => {
                    const { data } = error;

                    let message = (
                        <Typography sx={{ color: 'grey.600' }}>
                            {data.message || 'Ocorreu um erro ao finalizar o pedido. Tente novamente.'}
                        </Typography>
                    );

                    if (applyStockErrorText(data.errors)) {
                        message = <CarrinhoStockErrorText products={data.errors} />;
                    }

                    contentFinishedErrorModal.current = message;

                    setOpenFinishedErrorModal(true);
                },
                onSettled: () => setDisabledActions(false),
            },
        );
    };

    /**
     * - finaliza todos os pedidos selecionados ou todos os pedidos do carrinho.
     * - se sucesso, exibe modal de sucesso e atualiza dados do carrinho.
     * - se erro, exibe modal de erro informando para ver erro em cada pedido.
     */
    const mutateFinishAllPedidos = () => {
        const idsPedidos = selectedPedidos.length ? selectedPedidos.map((pedido) => pedido.idPedido) : pedidos.map((pedido) => pedido.idPedido) || [];

        setFinishingPedidoIds(idsPedidos);
        setDisabledActions(true);

        mutate(
            { url: `/compra/pedidocompra/rascunho/finalizar?idsPedido=${idsPedidos.join('&idsPedido=')}`, preventSnack: true },
            {
                onSuccess: () => {
                    setIsFinishAllPolling(true);

                    queryClient.invalidateQueries({ queryKey: ['carrinho'] });
                },
                onError: () => {
                    contentFinishedErrorModal.current = (
                        <Typography sx={{ color: 'grey.600' }}>
                            Alguns pedidos não foram finalizados com sucesso.
                            <br />
                            Verifique o erro clicando em "Falha ao Finalizar" em cada pedido.
                        </Typography>
                    );

                    setOpenFinishedErrorModal(true);
                    setDisabledActions(false);
                    setFinishingPedidoIds([]);

                    queryClient.invalidateQueries({ queryKey: ['carrinho'] });
                    queryClient.invalidateQueries({ queryKey: ['carrinho-totalizador'] });
                },
            },
        );
    };

    /**
     * - fluxo de finalizar pedido.
     * - se pedido tiver fgTelaPagamento true, redireciona para tela de pagamento.
     * - se não, finaliza o pedido diretamente pela API.
     */
    const finishPedido = () => {
        if (!row) return;

        const { fgTelaPagamento, idPedido } = row;

        if (fgTelaPagamento) {
            redirectToPagamento([idPedido]);
        } else {
            mutateFinishPedido();
        }
    };

    /**
     * - fluxo de finalizar todos os pedidos selecionados ou todos os pedidos do carrinho.
     * - se algum pedido tiver fgTelaPagamento true, redireciona para tela de pagamento passando os ids dos pedidos.
     * - se não, finaliza os pedidos diretamente pela API.
     */
    const finishAllPedidos = () => {
        const needToRedirect = pedidos.some((pedido) => pedido.fgTelaPagamento);

        if (needToRedirect) {
            const idsPedido = pedidos.map((pedido) => pedido.idPedido).reverse();

            redirectToPagamento(idsPedido);
        } else {
            mutateFinishAllPedidos();
        }
    };

    return { finishPedido, finishAllPedidos, finishingPedidosCount: finishingPedidoIds.length };
}
